import React, { useMemo, useCallback, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useStacklineTheme, StacklineTheme } from '@stackline/ui';
import ReactDOMServer from 'react-dom/server';
import _merge from 'lodash/merge';
import HoverTooltip from '../HoverTooltip/HoverTooltip';
import {
  useMetricFormatter,
  useAppSelector,
  useWeekRange,
  useComparisonPeriod,
  WeekRange,
  ComparisonPeriod,
  useQueryParamValue
} from 'src/utils/Hooks';
import { BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { getWeekIdRange, getNextWeekId } from 'src/utils/dateUtils';
import { ValueOf } from 'sl-api-connector';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { calculatePercentChange } from 'src/utils/app';
import { useSplineChartV2Data } from './useSplineChartV2Data';
import { formatIsoDateToMMDD, SplineChartV2DataPointWithMeta, weekIdToMeta } from './splineChartV2Utils';
import { useRegisterChartContext } from 'src/utils/chartContext/useRegisterChartContext';
import type { ChartLegendMetadata } from 'src/utils/chartContext/types';
import { Box } from '@mui/system';
import {
  CHART_SERIES_OPACITY_TRANSITION_SX,
  useChartLegendVisibility
} from 'src/components/BeaconV2Components/BeaconChartWithLegend/ChartLegendVisibilityContext';

/**
 * Convert hex color to RGB string for use in rgba()
 */
const hexToRgb = (hex: string): string => {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Handle shorthand hex (e.g., #fff)
  const fullHex =
    cleanHex.length === 3
      ? cleanHex
          .split('')
          .map((c) => c + c)
          .join('')
      : cleanHex;

  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);

  return `${r}, ${g}, ${b}`;
};

/**
 * Metadata for a single week
 */
export interface WeekMeta {
  year: number;
  week: number;
  weekLabel: string;
  ts: number; // timestamp (ms)
  isoDate: string; // YYYY-MM-DD
}

/**
 * A single data point for the spline chart.
 * This is the simplified format that consumers will provide.
 */
export interface SplineChartV2DataPoint {
  /** Week ID in format YYYYWW (e.g., 202501) */
  weekId: number;
  /** The Y value for this data point */
  value: number | null;
  /** Optional custom name for the point (defaults to week number) */
  name?: string;
}

/**
 * Zone configuration for dash style changes at different x positions.
 * Uses category index (0-based) for zone boundaries.
 */
export interface SplineChartV2Zone {
  /** The category index where this zone ends (exclusive). Omit for the last zone. */
  value?: number;
  /** Dash style for this zone */
  dashStyle?: Highcharts.DashStyleValue;
  /** Optional color override for this zone */
  color?: string;
}

/**
 * Series configuration for the chart
 */
export interface SplineChartV2Series {
  /** Unique identifier for the series */
  id: string;
  /** Display name for the series */
  name: string;
  /** Data points for this series */
  data: SplineChartV2DataPoint[];
  /** Custom color for the series (optional, will use theme defaults) */
  color?: string;
  /** Whether this series should be rendered as dashed (e.g., for forecasts) */
  isDashed?: boolean;
  /** Z-index for layering (higher = on top) */
  zIndex?: number;
  /**
   * Optional zones for changing dash style at different x positions.
   * Zone values are category indices (0-based).
   * Example: [{ value: 5, dashStyle: 'Dash' }, { value: 10, dashStyle: 'Solid' }, { dashStyle: 'Dot' }]
   */
  zones?: SplineChartV2Zone[];
  /** Axis to apply zones on. Defaults to 'x' if zones are provided. */
  zoneAxis?: 'x' | 'y';
}

export interface SplineChartV2Props {
  /** Primary series data */
  primarySeries: SplineChartV2Series;
  /** Optional comparison/secondary series */
  comparisonSeries?: SplineChartV2Series;
  /** Optional forecast series (rendered as dashed line) */
  forecastSeries?: SplineChartV2Series;
  /** Metric type for formatting values */
  metricType: ValueOf<typeof METRICTYPE>;
  /** Preview mode dimensions */
  preview?: 'large' | 'small' | { width: number; height: number };
  /** Chart width */
  width?: number | string;
  /** Remove spacing around the chart */
  removeSpacing?: boolean;
  /** Placeholder to show when there's no data */
  NoDataPlaceholder?: React.ReactNode;
  /** Optional Highcharts options override */
  chartOptionsOverride?: Partial<Highcharts.Options>;
  /**
   * Custom tooltip renderer. If not provided, uses the default HoverTooltip.
   */
  renderCustomTooltip?: (params: {
    primaryPoint: SplineChartV2DataPointWithMeta;
    comparisonPoint?: SplineChartV2DataPointWithMeta;
    percentChange?: number;
    theme: StacklineTheme;
    formatMetric: ReturnType<typeof useMetricFormatter>;
    metricType: ValueOf<typeof METRICTYPE>;
  }) => string;
  /**
   * Whether to show markers on the last data point
   */
  showLastPointMarker?: boolean;
  /**
   * Optional explicit override for category start week ID.
   * When provided, bypasses the automatic full-year expansion for prior year comparisons.
   * Useful for components with custom time periods (e.g. MicroTrend).
   */
  categoryStartWeekId?: number;
  /**
   * Optional explicit override for category end week ID.
   * Must be provided together with categoryStartWeekId.
   */
  categoryEndWeekId?: number;
  /**
   * Optional override for prior period detection.
   * When true, merges comparison and primary into one continuous line.
   * If undefined, detection is based on Redux comparisonTimePeriod.id.
   */
  isPriorPeriodOverride?: boolean;
  /**
   * Optional: Chart context ID for Advisor integration.
   * When provided, the chart options will be registered with the chart context store.
   * Example: 'total-traffic-spline'
   */
  chartContextId?: string;
  /**
   * Optional: Pre-computed legend summary values as displayed in the UI (main period value,
   * comparison value, percent change, direction). Provided by AtlasV2DorisSplineChart
   * so the Advisor agent can read the badge values without re-deriving them from raw series data.
   */
  legendMetadata?: ChartLegendMetadata;
  /**
   * When enabled, hovering over a zone will reduce the opacity of other zones.
   * This helps highlight the currently hovered zone segment.
   */
  enableZoneHoverEffect?: boolean;
  /**
   * Opacity to apply to non-hovered zones when enableZoneHoverEffect is true.
   * Defaults to 0.3.
   */
  zoneHoverInactiveOpacity?: number;
}

/**
 * SplineChartV2 - A simplified spline chart that uses categories-based xAxis
 *
 * Key differences from SplineChart:
 * - Uses categories (week labels) instead of timestamp-based x positioning
 * - Simpler data format: just { weekId, value } objects
 * - Dynamically generates categories from the time period
 * - Less data transformation required from API responses
 */
const SplineChartV2 = ({
  primarySeries,
  comparisonSeries,
  forecastSeries,
  metricType,
  preview,
  width,
  removeSpacing = false,
  NoDataPlaceholder,
  chartOptionsOverride,
  renderCustomTooltip,
  showLastPointMarker = false,
  categoryStartWeekId,
  categoryEndWeekId,
  isPriorPeriodOverride,
  chartContextId,
  legendMetadata,
  enableZoneHoverEffect = false,
  zoneHoverInactiveOpacity = 0.3
}: SplineChartV2Props) => {
  const theme = useStacklineTheme();
  const formatMetric = useMetricFormatter();
  const chartRef = useRef<Highcharts.Chart | null>(null);

  // Get time period info for internal marker logic (like V1)
  const weekRange = useWeekRange();
  const comparisonPeriod = useComparisonPeriod();
  const tab = useQueryParamValue('tab');

  // Internal logic to determine if we should show last point marker (matches V1 behavior)
  const isLastWeekWithComparisonMarker = useMemo(() => {
    return (
      tab !== 'forecasts' &&
      tab !== BEACON_TABS.ADVERTISING &&
      weekRange === WeekRange.LAST_WEEK &&
      (comparisonPeriod === ComparisonPeriod.PRIOR_YEAR || comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD)
    );
  }, [comparisonPeriod, weekRange, tab]);

  // Combine prop with internal logic
  const shouldShowLastPointMarker = showLastPointMarker || isLastWeekWithComparisonMarker;

  // Use the data transformation hook
  const {
    isPriorPeriod,
    mergedData,
    categoryCount,
    xAxisCategories,
    primaryDataMapped,
    comparisonDataMapped,
    forecastDataMapped,
    comparisonLength,
    primaryWeekIdsForCategories,
    primaryStartWeekId
  } = useSplineChartV2Data({
    primarySeries,
    comparisonSeries,
    forecastSeries,
    categoryStartWeekId,
    categoryEndWeekId,
    isPriorPeriodOverride
  });

  // Series visibility and opacity driven by the legend in BeaconChartWithLegend (click selects a
  // series, hover dims the other). Defaults to everything visible when rendered outside that wrapper.
  const { primaryVisible, comparisonVisible, primaryOpacity, comparisonOpacity } = useChartLegendVisibility();
  // Applies a legend-hover dim to a hex color (used where a series is colored through zones)
  const dimColor = (color: string, opacity: number) => (opacity === 1 ? color : `rgba(${hexToRgb(color)}, ${opacity})`);

  // Merged (prior period) mode renders comparison + primary as one line, so hiding a series
  // means blanking out its portion of the merged data instead of hiding a Highcharts series.
  const visibleMergedData = useMemo(() => {
    if (!mergedData) return mergedData;
    if (primaryVisible && comparisonVisible) return mergedData;
    return mergedData.map((point, index) => {
      const isComparisonPortion = index < comparisonLength;
      const isHidden = isComparisonPortion ? !comparisonVisible : !primaryVisible;
      return isHidden ? { ...point, value: null } : point;
    });
  }, [mergedData, comparisonLength, primaryVisible, comparisonVisible]);

  // Chart dimensions
  const chartDimensionsMap = useMemo(
    () => ({
      large: { width: width || 299, height: 85 },
      small: { width: width || 178, height: 85 },
      custom: typeof preview === 'object' ? preview : undefined
    }),
    [width, preview]
  );

  // Calculate y-axis bounds from plotted values
  // Only visible series contribute so the axis re-scales when a legend is toggled off.
  const { chartMin, hasOnlyZeroValues } = useMemo(() => {
    const allYValues = [
      ...(primaryVisible ? primaryDataMapped.map((p) => p?.value ?? 0) : []),
      ...(comparisonVisible ? comparisonDataMapped.map((p) => p?.value ?? 0) : []),
      ...(primaryVisible ? forecastDataMapped.map((p) => p?.value ?? 0) : [])
    ].filter((v) => v !== null);
    const min = allYValues.length > 0 ? Math.min(...allYValues) : 0;
    const max = allYValues.length > 0 ? Math.max(...allYValues) : 0;

    return {
      chartMin: min,
      hasOnlyZeroValues: allYValues.length > 0 && min === 0 && max === 0
    };
  }, [primaryDataMapped, comparisonDataMapped, forecastDataMapped, primaryVisible, comparisonVisible]);

  // Helper to determine which zone a point belongs to based on its index
  const getZoneIndexForPoint = useCallback((pointIndex: number, zones: SplineChartV2Zone[] | undefined): number => {
    if (!zones || zones.length === 0) return -1;
    for (let i = 0; i < zones.length; i++) {
      const zone = zones[i];
      if (zone.value === undefined || pointIndex < zone.value) {
        return i;
      }
    }
    return zones.length - 1;
  }, []);

  // Animation duration for hover effects (in ms)
  const hoverAnimationDuration = 200;

  // Apply zone hover effect - dims zones that are not being hovered and applies hover state
  const applyZoneHoverEffect = useCallback(
    (chart: Highcharts.Chart, hoveredZoneIndex: number | null, seriesZones: SplineChartV2Zone[] | undefined) => {
      if (!enableZoneHoverEffect || !seriesZones || seriesZones.length === 0) return;

      const series = chart.series[0] as Highcharts.Series & {
        update: (
          options: Partial<Highcharts.SeriesSplineOptions>,
          redraw?: boolean,
          animation?: boolean | Partial<Highcharts.AnimationOptionsObject>
        ) => void;
      };
      if (!series) return;

      const updatedZones = seriesZones.map((zone, index) => {
        const baseColor = zone.color || primarySeries.color || theme.colors.primary;
        if (hoveredZoneIndex === null) {
          // No hover - reset all zones to full opacity
          return {
            value: zone.value,
            dashStyle: zone.dashStyle,
            color: baseColor
          };
        }
        // Apply reduced opacity to non-hovered zones
        const isHovered = index === hoveredZoneIndex;
        return {
          value: zone.value,
          dashStyle: zone.dashStyle,
          color: isHovered ? baseColor : `rgba(${hexToRgb(baseColor)}, ${zoneHoverInactiveOpacity})`
        };
      });

      // Update zones with animation
      series.update({ zones: updatedZones }, true, { duration: hoverAnimationDuration, easing: 'easeOutQuad' });

      // Apply hover state after redraw to ensure it takes effect
      requestAnimationFrame(() => {
        if (hoveredZoneIndex !== null) {
          series.setState('hover');
        } else {
          series.setState('');
        }
      });
    },
    [enableZoneHoverEffect, primarySeries.color, theme.colors.primary, zoneHoverInactiveOpacity]
  );

  const textStyles: Highcharts.CSSObject = useMemo(
    () => ({
      fontFamily: theme.text.fontFamily,
      color: theme.colors.primary,
      fontSize: '12px'
    }),
    [theme.text.fontFamily, theme.colors.primary]
  );

  const seriesMarker = useMemo(
    () => ({
      enabled: false,
      symbol: 'circle' as const,
      states: {
        hover: {
          radius: 4,
          lineWidth: 0
        }
      }
    }),
    []
  );

  // Only use pointPlacement: 'on' when there's more than 1 data point
  // With just 1 point, pointPlacement can cause issues
  const shouldUsePointPlacement = categoryCount > 1;

  const previewOptions: Partial<Highcharts.Options> = {
    chart: {
      ...(chartDimensionsMap[typeof preview === 'object' ? 'custom' : preview] || {}),
      marginBottom: 10,
      backgroundColor: 'transparent'
    },
    xAxis: {
      visible: false,
      height: 0,
      minPadding: 0,
      maxPadding: 0
    },
    yAxis: [{ visible: false }],
    plotOptions: {
      series: {
        lineWidth: 1.5,
        marker: {
          enabled: true
        },
        ...(shouldUsePointPlacement ? { pointPlacement: 'on' } : {})
      }
    }
  };
  // Build series configuration

  const series: Highcharts.SeriesOptionsType[] = useMemo(() => {
    const result: Highcharts.SeriesOptionsType[] = [];

    // Forecast series (dashed)
    if (forecastSeries && forecastDataMapped.length > 0) {
      result.push({
        type: 'spline',
        name: forecastSeries.name,
        data: forecastDataMapped.map((point) => ({
          name: point?.weekId ? String(point.weekId) : '',
          y: point?.value ?? null,
          weekId: point?.weekId ?? 0,
          meta: point?.meta
        })),
        dashStyle: 'Dash',
        color: forecastSeries.color || theme.colors.primary,
        marker: seriesMarker,
        enableMouseTracking: true,
        zIndex: forecastSeries.zIndex ?? 1,
        // The forecast continues the primary line, so it follows the primary legend toggle
        visible: primaryVisible,
        opacity: primaryOpacity
      } as Highcharts.SeriesSplineOptions);
    }

    // Merged mode: single continuous line (comparison → primary) with color zones
    if (isPriorPeriod && visibleMergedData && comparisonSeries) {
      result.push({
        type: 'spline',
        name: primarySeries.name,
        data: visibleMergedData.map((point) => ({
          name: point.weekId ? String(point.weekId) : '',
          y: point.value ?? null,
          weekId: point.weekId ?? 0,
          meta: point.meta
        })),
        color: primarySeries.color || theme.colors.primary,
        marker: seriesMarker,
        zIndex: primarySeries.zIndex ?? 2,
        // Use zones to color comparison portion with secondary color
        zoneAxis: 'x',
        zones: [
          {
            // Comparison portion (indices 0 to comparisonLength - 1)
            value: comparisonLength,
            color: dimColor(comparisonSeries.color || theme.colors.secondary, comparisonOpacity)
          },
          {
            // Primary portion (indices comparisonLength onwards)
            color: dimColor(primarySeries.color || theme.colors.primary, primaryOpacity)
          }
        ]
      } as Highcharts.SeriesSplineOptions);
    } else {
      // Standard mode: separate primary and comparison series

      // Primary series
      // Primary series - no point-level markers, using renderer instead
      const primarySeriesOptions: Highcharts.SeriesSplineOptions = {
        type: 'spline',
        name: primarySeries.name,
        data: primaryDataMapped.map((point) => ({
          name: point?.weekId ? String(point.weekId) : '',
          y: point?.value ?? null,
          weekId: point?.weekId ?? 0,
          meta: point?.meta
        })),
        color: primarySeries.color || theme.colors.primary,
        marker: seriesMarker,
        zIndex: primarySeries.zIndex ?? 2,
        visible: primaryVisible,
        opacity: primaryOpacity
      };

      // Apply zones if provided (for dash style changes at different x positions)
      if (primarySeries.zones && primarySeries.zones.length > 0) {
        primarySeriesOptions.zoneAxis = primarySeries.zoneAxis || 'x';
        primarySeriesOptions.zones = primarySeries.zones.map((zone) => ({
          value: zone.value,
          dashStyle: zone.dashStyle,
          color: zone.color
        }));
      }

      result.push(primarySeriesOptions);

      // Comparison series - no markers
      if (comparisonSeries && comparisonDataMapped.length > 0) {
        const comparisonSeriesOptions: Highcharts.SeriesSplineOptions = {
          type: 'spline',
          name: comparisonSeries.name,
          data: comparisonDataMapped.map((point) => ({
            name: point?.weekId ? String(point.weekId) : '',
            y: point?.value ?? null,
            weekId: point?.weekId ?? 0,
            meta: point?.meta
          })),
          color: comparisonSeries.color || theme.colors.secondary,
          marker: seriesMarker,
          zIndex: comparisonSeries.zIndex ?? 0,
          visible: comparisonVisible,
          opacity: comparisonOpacity
        };

        // Apply zones if provided for comparison series
        if (comparisonSeries.zones && comparisonSeries.zones.length > 0) {
          comparisonSeriesOptions.zoneAxis = comparisonSeries.zoneAxis || 'x';
          comparisonSeriesOptions.zones = comparisonSeries.zones.map((zone) => ({
            value: zone.value,
            dashStyle: zone.dashStyle,
            color: zone.color
          }));
        }

        result.push(comparisonSeriesOptions);
      }
    }

    return result;
  }, [
    forecastSeries,
    forecastDataMapped,
    isPriorPeriod,
    visibleMergedData,
    primarySeries,
    primaryDataMapped,
    comparisonSeries,
    comparisonDataMapped,
    comparisonLength,
    theme.colors.primary,
    theme.colors.secondary,
    seriesMarker,
    primaryVisible,
    comparisonVisible,
    primaryOpacity,
    comparisonOpacity
  ]);

  // Primary series color for the marker
  const primaryColor = primarySeries?.color || theme.colors.primary;

  const options: Highcharts.Options = _merge(
    {
      chart: {
        type: 'spline',
        ...(removeSpacing ? { spacing: [0, 0, 0, 0] } : {}),
        spacingLeft: 0,
        events: {
          render(this: Highcharts.Chart) {
            // Only show marker for Last Week + Prior Year, and only while the primary series is visible
            if (!shouldShowLastPointMarker || !primaryVisible) {
              // Remove existing marker if we shouldn't show it
              if ((this as any).customMarker) {
                (this as any).customMarker.destroy();
                (this as any).customMarker = null;
              }
              return;
            }

            // Get the primary series by name (not comparison)
            const allSplineSeries = this.series.filter((s) => s.type === 'spline' && s.visible);
            // Find the primary series - it should have the primary series name and highest zIndex
            const primarySeriesObj = allSplineSeries.find((s) => (s.options as any).zIndex >= 2) || allSplineSeries[0];
            if (!primarySeriesObj || !primarySeriesObj.points.length) return;

            const points = primarySeriesObj.points;
            // Find the last point that has actual data (not null)
            let lastPoint: (Highcharts.Point & { plotX?: number; plotY?: number }) | null = null;
            for (let i = points.length - 1; i >= 0; i--) {
              if (points[i].y !== null && points[i].y !== undefined) {
                lastPoint = points[i] as Highcharts.Point & { plotX?: number; plotY?: number };
                break;
              }
            }
            if (!lastPoint) return;

            const x = this.plotLeft + (lastPoint.plotX ?? 0);
            const y = this.plotTop + (lastPoint.plotY ?? 0);

            if (!(this as any).customMarker) {
              // First render - create the marker
              (this as any).customMarker = this.renderer
                .circle(x, y, 5)
                .attr({ fill: primaryColor, zIndex: 5, opacity: primaryOpacity })
                .add();
            } else {
              // Redraw - move the marker
              (this as any).customMarker.attr({ cx: x, cy: y, opacity: primaryOpacity });
            }
          }
        }
      },
      title: { text: '' },
      exporting: { enabled: false },
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: {
        enabled: true,
        shared: true,
        shadow: false,
        hideDelay: 0,
        outside: true,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        useHTML: true,
        formatter() {
          // For merged prior period mode, handle single series with zones
          if (isPriorPeriod && comparisonSeries) {
            const point = this.points?.[0];
            if (!point) return '';

            const pointIndex = point.point.index;
            const isComparisonPortion = pointIndex < comparisonLength;

            const meta = (point.point as any).meta as WeekMeta;
            const value = point.y ?? null;

            // Determine the correct color based on which portion we're in
            const tooltipColor = isComparisonPortion
              ? comparisonSeries.color || theme.colors.secondary
              : primarySeries.color || theme.colors.primary;

            return ReactDOMServer.renderToStaticMarkup(
              <HoverTooltip
                theme={theme}
                title={`Week ${meta?.weekLabel ?? ''}`}
                primaryColor={tooltipColor}
                primaryMetric={
                  value !== null
                    ? formatMetric(value, metricType, {
                        decimalPlaces: 2,
                        showFullValue: metricType === METRICTYPE.PERCENT,
                        showNegative: true
                      })
                    : undefined
                }
                primaryMetricLabel={meta?.isoDate ? formatIsoDateToMMDD(meta.isoDate) : undefined}
                tooltipWrapperStyle={{ width: '172px' }}
              />
            );
          }

          // Standard mode: find primary, comparison, and forecast points
          const primaryPoint = this.points?.find((p) => p.series.name === primarySeries.name) as
            | (Highcharts.TooltipFormatterContextObject & {
                point: { weekId: number; meta: WeekMeta };
              })
            | undefined;

          const comparisonPoint = comparisonSeries
            ? (this.points?.find((p) => p.series.name === comparisonSeries.name) as
                | (Highcharts.TooltipFormatterContextObject & {
                    point: { weekId: number; meta: WeekMeta };
                  })
                | undefined)
            : undefined;

          const forecastPoint = forecastSeries
            ? (this.points?.find((p) => p.series.name === forecastSeries.name) as
                | (Highcharts.TooltipFormatterContextObject & {
                    point: { weekId: number; meta: WeekMeta };
                  })
                | undefined)
            : undefined;

          // Need at least one point to show tooltip
          if (!primaryPoint && !comparisonPoint && !forecastPoint) return '';

          // For forecasts: if no primary but have forecast, use forecast as the "primary" display
          // This handles the case where primaryData is empty but forecastData exists
          const effectivePrimaryPoint = primaryPoint || forecastPoint;

          // Get the category index (x-axis position) - use point.index for numeric index
          // this.x returns the category label (string), not the index
          const categoryIndex = (this.points?.[0]?.point as any)?.index ?? 0;

          // Get weekId and meta from whichever point is available
          const activePoint = effectivePrimaryPoint || comparisonPoint;

          // Compute the expected primary weekId for this category index
          // This handles 52/53 week year mapping correctly
          // (e.g., category index 3 with start 202601 = 202604, even if comparison shows 202505)
          // Priority: 1) actual primary data, 2) computed from start + index, 3) fallback to activePoint
          const primaryDataAtIndex = primaryDataMapped[categoryIndex];
          const primaryWeekIdForCategory = primaryDataAtIndex?.weekId ?? primaryWeekIdsForCategories[categoryIndex];

          // Use primary weekId for tooltip title if available, otherwise fall back to activePoint's meta
          const tooltipMeta = weekIdToMeta(primaryWeekIdForCategory);

          const primaryDataPoint: SplineChartV2DataPointWithMeta | undefined = effectivePrimaryPoint
            ? {
                weekId: (effectivePrimaryPoint.point as any).weekId,
                value: effectivePrimaryPoint.y ?? null,
                meta: (effectivePrimaryPoint.point as any).meta as WeekMeta
              }
            : undefined;

          const comparisonDataPoint: SplineChartV2DataPointWithMeta | undefined = comparisonPoint
            ? {
                weekId: (comparisonPoint.point as any).weekId,
                value: comparisonPoint.y ?? null,
                meta: (comparisonPoint.point as any).meta as WeekMeta
              }
            : undefined;

          const percentChange =
            comparisonDataPoint &&
            primaryDataPoint &&
            comparisonDataPoint.value !== null &&
            primaryDataPoint.value !== null
              ? calculatePercentChange(primaryDataPoint.value, comparisonDataPoint.value)
              : undefined;

          // Use custom renderer if provided
          if (renderCustomTooltip && primaryDataPoint) {
            return renderCustomTooltip({
              primaryPoint: primaryDataPoint,
              comparisonPoint: comparisonDataPoint,
              percentChange,
              theme,
              formatMetric,
              metricType
            });
          }

          // Determine primary color - use forecast color if showing forecast point, otherwise primary color
          const primaryTooltipColor = effectivePrimaryPoint
            ? (effectivePrimaryPoint.color as string) || theme.colors.primary
            : theme.colors.primary;

          // Default tooltip - show whatever data is available
          return ReactDOMServer.renderToStaticMarkup(
            <HoverTooltip
              theme={theme}
              title={`Week ${tooltipMeta.weekLabel}`}
              primaryColor={primaryTooltipColor}
              primaryMetric={
                primaryDataPoint
                  ? formatMetric(primaryDataPoint.value, metricType, {
                      decimalPlaces: 2,
                      showFullValue: metricType === METRICTYPE.PERCENT,
                      showNegative: true
                    })
                  : undefined
              }
              primaryMetricLabel={
                primaryDataPoint?.meta.isoDate ? formatIsoDateToMMDD(primaryDataPoint.meta.isoDate) : undefined
              }
              change={
                percentChange !== undefined
                  ? formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })
                  : undefined
              }
              rawChange={percentChange}
              secondaryMetric={
                comparisonDataPoint
                  ? formatMetric(comparisonDataPoint.value, metricType, {
                      decimalPlaces: 2,
                      showFullValue: metricType === METRICTYPE.PERCENT,
                      showNegative: true
                    })
                  : undefined
              }
              secondaryMetricLabel={
                comparisonDataPoint?.meta.isoDate ? formatIsoDateToMMDD(comparisonDataPoint.meta.isoDate) : undefined
              }
              secondaryColor={comparisonPoint ? (comparisonPoint.color as string) : theme.colors.secondary}
              tooltipWrapperStyle={{ width: '172px' }}
            />
          );
        }
      },
      xAxis: {
        type: 'category',
        tickLength: 0,
        lineColor: 'transparent',
        categories: xAxisCategories,
        labels: {
          style: textStyles,
          rotation: 0,
          enabled: true,
          // Ensure labels show even with many categories
          step: Math.max(1, Math.floor(xAxisCategories.length / 12)), // Show ~12 labels max
          // Hide the first x-axis label to avoid overlap / reduce clutter
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            if (this.pos === 0) return '';
            return this.value;
          }
        },
        visible: true,
        title: { text: '' },
        // Ensure axis line is visible (for debugging)
        lineWidth: 1,
        // Ensure points span full width (especially important for 2-point charts)
        minPadding: 0,
        maxPadding: 0
      },
      yAxis: [
        {
          gridLineWidth: 0,
          title: { text: '' },
          labels: {
            formatter() {
              return formatMetric(Number(this.value), metricType, {
                decimalPlaces: 2,
                showFullValue: false,
                showNegative: true
              });
            },
            style: textStyles
          },
          min: Math.min(0, chartMin),
          max: hasOnlyZeroValues ? (metricType === METRICTYPE.PERCENT ? 0.06 : 6) : undefined,
          tickAmount: hasOnlyZeroValues ? 7 : undefined,
          tickInterval: hasOnlyZeroValues ? (metricType === METRICTYPE.PERCENT ? 0.01 : 1) : undefined
        }
      ],
      plotOptions: {
        spline: {
          // Keep gaps at null (do not connect across missing data)
          connectNulls: true,
          // Round line ends at segment boundaries for smoother visual at gaps
          linecap: 'round'
        },
        series: {
          ...(shouldUsePointPlacement ? { pointPlacement: 'on' } : {}),
          // Add hover state for line width increase (preserves dash patterns) with easing
          states: {
            hover: {
              lineWidthPlus: 1,
              animation: {
                duration: 200
              }
            },
            inactive: {
              animation: {
                duration: 200
              }
            },
            normal: {
              animation: {
                duration: 200
              }
            }
          },
          point: {
            events: {
              mouseOver(this: Highcharts.Point) {
                if (!enableZoneHoverEffect || !primarySeries.zones || primarySeries.zones.length === 0) return;
                const pointIndex = this.index;
                const zoneIndex = getZoneIndexForPoint(pointIndex, primarySeries.zones);
                if (chartRef.current) {
                  applyZoneHoverEffect(chartRef.current, zoneIndex, primarySeries.zones);
                }
              },
              mouseOut() {
                if (!enableZoneHoverEffect || !primarySeries.zones || primarySeries.zones.length === 0) return;
                if (chartRef.current) {
                  applyZoneHoverEffect(chartRef.current, null, primarySeries.zones);
                }
              }
            }
          }
        }
      },
      series
    } as Highcharts.Options,
    preview ? previewOptions : {},
    chartOptionsOverride || {}
  );

  // Register chart options with the chart context store for Advisor integration
  useRegisterChartContext(chartContextId, options as Record<string, unknown>, true, legendMetadata);
  // Callback to store chart reference
  const chartCallback = useCallback((chart: Highcharts.Chart) => {
    chartRef.current = chart;
  }, []);

  // Based on the fetched data (not the rendered series) so that hiding a series via the legend
  // toggle never swaps the chart for the no-data placeholder.
  const noSeriesData = [...primaryDataMapped, ...comparisonDataMapped, ...forecastDataMapped].every(
    (point) => point?.value === null || point?.value === undefined
  );

  return noSeriesData && NoDataPlaceholder ? (
    <>{NoDataPlaceholder}</>
  ) : (
    <Box sx={CHART_SERIES_OPACITY_TRANSITION_SX}>
      <HighchartsReact highcharts={Highcharts} options={options} callback={chartCallback} />
    </Box>
  );
};

export default SplineChartV2;

/**
 * Utility hook to generate week IDs for the current time period
 */
export const useWeekIdsForTimePeriod = () => {
  const startWeekId = useAppSelector((state) => state.mainTimePeriod.startWeek);
  const endWeekId = useAppSelector((state) => state.mainTimePeriod.endWeek);

  return useMemo(() => {
    if (!startWeekId || !endWeekId) return [];
    return getWeekIdRange(startWeekId, endWeekId);
  }, [startWeekId, endWeekId]);
};
